import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useI18n, LANGS } from "@/i18n";
import { Wordmark } from "./Logo";
import { scrollToId } from "./useLenis";
import { EASE } from "./Reveal";

export const NAV_IDS = ["about", "services", "approach", "sectors", "contact"];

export const useGo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => (pathname !== "/" ? navigate(`/#${id}`) : scrollToId(id));
};

export const LangSwitch = ({ className = "", prefix = "lang" }) => {
  const { lang, setLang, t } = useI18n();
  return (
    <div role="group" aria-label={t.nav.lang} className={`flex items-center rounded-full border border-white/15 p-0.5 font-mono text-xs ${className}`}>
      {LANGS.map((l) => (
        <button
          key={l}
          type="button"
          data-testid={`${prefix}-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors duration-300 ${lang === l ? "bg-bone text-ink" : "text-zinc-300 hover:text-bone"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const MobileMenu = ({ onPick, onClose }) => {
  const { t } = useI18n();
  return (
    <motion.div
      data-testid="mobile-menu"
      initial={{ clipPath: "inset(0 0 100% 0)" }}
      animate={{ clipPath: "inset(0 0 0% 0)" }}
      exit={{ clipPath: "inset(0 0 100% 0)" }}
      transition={{ duration: 0.7, ease: EASE }}
      className="fixed inset-0 z-[70] flex flex-col bg-ink bg-grid lg:hidden"
    >
      <div className="wrap flex h-16 items-center justify-between">
        <Wordmark />
        <button data-testid="mobile-menu-close" onClick={onClose} aria-label={t.nav.close} className="grid h-11 w-11 place-items-center rounded-full border border-white/15"><X className="h-5 w-5" /></button>
      </div>
      <nav className="wrap mt-10 flex flex-col">
        {NAV_IDS.map((id, i) => (
          <motion.button
            key={id}
            data-testid={`mobile-nav-${id}`}
            onClick={() => onPick(id)}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.06, duration: 0.6, ease: EASE }}
            className="flex items-baseline gap-4 border-b border-white/10 py-5 text-left"
          >
            <span className="font-mono text-xs text-signal">0{i + 1}</span>
            <span className="font-display text-4xl font-semibold tracking-tight">{t.nav[id]}</span>
          </motion.button>
        ))}
      </nav>
      <div className="wrap mt-auto pb-10"><LangSwitch prefix="mobile-lang" className="w-fit" /></div>
    </motion.div>
  );
};

export const Header = () => {
  const { t } = useI18n();
  const go = useGo();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const pick = (id) => {
    setOpen(false);
    setTimeout(() => go(id), 50);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-500 ${scrolled ? "border-white/10 bg-ink/75 backdrop-blur-xl" : "border-transparent"}`}
      >
        <div className="wrap flex h-16 items-center justify-between lg:h-20">
          <Link to="/" data-testid="header-logo" aria-label="GridJob OÜ — home"><Wordmark /></Link>
          <nav className="hidden items-center gap-9 lg:flex" aria-label="Main">
            {NAV_IDS.map((id) => (
              <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)} className="nav-link text-sm text-zinc-300 hover:text-bone">{t.nav[id]}</button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LangSwitch className="hidden sm:flex" />
            <button
              data-testid="header-cta"
              onClick={() => go("contact")}
              className="group hidden items-center gap-2 rounded-full bg-bone px-5 py-2.5 text-sm font-medium text-ink transition-colors duration-300 hover:bg-signal md:flex"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button data-testid="mobile-menu-open" onClick={() => setOpen(true)} aria-label={t.nav.menu} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onPick={pick} onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
